import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useRef} from 'react';
import React from 'react';
import {CUSTOM_CONTROLS_WRAPPER} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {CustomOutlineBoxes} from '@Components/poster-editor/components/custom-item-controls/components/custom-outline-boxes/custom-outline-boxes';
import {SingleItemCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls';
import {ActiveSelectionCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/active-selection-custom-controls/active-selection-custom-controls';
import {useAppSelector} from '@/hooks';
import type {CustomItemControlsWrapperProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import {CANVAS_BACKDROP_CLASS} from '@PosterWhiteboard/page/page.types';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import {useShouldHideControlsWhenItemAnimating} from '@Hooks/poster-editor/useShouldHideControlsWhenItemAnimating';

export function CustomItemControlsWrapper(props: CustomItemControlsWrapperProps): ReactElement | null {
  const isTouchGestureBeingApplied = useAppSelector((state) => state.posterEditor.isTouchGestureBeingApplied);
  const hideSelectedOrHoveredItemControlsDueToAnimation = useShouldHideControlsWhenItemAnimating(props.pageHashedId);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let animationFrameId: number | null = null;
    let mutationObserver: MutationObserver | null = null;
    const backdropSelector = `.${CANVAS_BACKDROP_CLASS}#${CSS.escape(props.pageHashedId)}`;

    const updateWrapperPosition = (backdrop: HTMLElement): void => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }

      const {top, left} = backdrop.getBoundingClientRect();
      wrapper.style.top = `${top}px`;
      wrapper.style.left = `${left}px`;
    };

    const startTrackingPosition = (backdrop: HTMLElement): void => {
      const track = (): void => {
        updateWrapperPosition(backdrop);

        animationFrameId = requestAnimationFrame(track);
      };

      track();
    };

    const observeForBackdropInsertion = (): void => {
      const rootToObserve = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER]?.get?.(0) ?? document.body;

      mutationObserver = new MutationObserver(() => {
        const backdrop = document.querySelector<HTMLElement>(backdropSelector);
        if (backdrop) {
          mutationObserver?.disconnect();

          startTrackingPosition(backdrop);
        }
      });

      mutationObserver.observe(rootToObserve, {childList: true, subtree: true});
    };

    observeForBackdropInsertion();

    return (): void => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      mutationObserver?.disconnect();
    };
  }, []);

  const getControlsWrapper = (): ReactElement | null => {
    const page = window.posterEditor?.whiteboard?.pages?.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }

    if (isTouchGestureBeingApplied) {
      return null;
    }

    if (hideSelectedOrHoveredItemControlsDueToAnimation) {
      return null;
    }

    return (
      <div
        ref={wrapperRef}
        style={{
          position: 'fixed',
          zIndex: 1,
        }}
        className={CUSTOM_CONTROLS_WRAPPER}
        key={CUSTOM_CONTROLS_WRAPPER}
        id={props.pageHashedId}
      >
        <CustomOutlineBoxes pageHashedId={props.pageHashedId} />
        <SingleItemCustomControls pageHashedId={props.pageHashedId} />
        <ActiveSelectionCustomControls pageHashedId={props.pageHashedId} />
      </div>
    );
  };

  return getControlsWrapper();
}
